import React, { useState, useEffect, useCallback, useContext } from 'react';
import {
  Box,
  Stepper,
  Step,
  StepLabel,
  Button,
  Typography,
  Paper,
  TextField,
  Checkbox,
  FormControlLabel,
  Alert,
  CircularProgress
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { witnessStatementService } from '../../services/api/witnessStatementService';
import { CognitoUserContext } from '../../contexts/cognitoUserContext';
import StatementReviewDisplay from './StatementReviewDisplay';
import TemplateRenderer from './TemplateRenderer';

const steps = [
  'System Context',
  'What Happened',
  'Identification',
  'Declaration & Submit'
];

const WitnessStatementForm = ({ template, statement: statementProp, eventData, onSave, onCancel }) => {
  const navigate = useNavigate();
  const { templateId: urlTemplateId, statementId: urlStatementId } = useParams();
  const { cognitoUser } = useContext(CognitoUserContext);

  // Use props if provided (dialog mode), otherwise use URL params (route mode)
  const templateId = template?.template_id || urlTemplateId;
  const statementId = statementProp?.statement_id || urlStatementId;
  const userEventId = eventData?.eventId;

  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [statement, setStatement] = useState(statementProp || null);
  const [formData, setFormData] = useState({
    system_context_affirmed: false,
    narrative: {
      stated_time: '',
      stated_position: '',
      account: ''
    },
    assertions: [],
    declaration: {
      text: 'I declare that this statement is true to the best of my knowledge and belief.',
      affirmed: false
    }
  });


  // Load statement if editing existing draft
  useEffect(() => {
    // If statement provided as prop without draft_data, fetch full statement
    if (statementProp && !statementProp.draft_data) {
      console.log('[WitnessStatementForm] Statement prop missing draft_data, fetching full statement');
      loadStatement(statementProp.statement_id);
      return;
    }

    // If statement provided as prop with draft_data, populate formData from it
    if (statementProp) {
      console.log('[WitnessStatementForm] Editing statement with draft_data');
      setStatement(statementProp);
      if (statementProp.draft_data) {
        setFormData({
          system_context_affirmed: statementProp.draft_data.system_context_affirmed_at ? true : false,
          narrative: statementProp.draft_data.narrative || formData.narrative,
          assertions: statementProp.draft_data.assertions || [],
          declaration: statementProp.draft_data.declaration || formData.declaration
        });
      }
      return;
    }

    if (statementId) {
      loadStatement(statementId);
    } else if (templateId) {
      createNewStatement();
    }
  }, [statementId, templateId, statementProp]);

  // Auto-save every 60 seconds
  useEffect(() => {
    if (statement && statement.status === 'draft') {
      const interval = setInterval(() => {
        saveProgress();
      }, 60000); // 60 seconds

      return () => clearInterval(interval);
    }
  }, [statement, formData]);

  const loadStatement = async (idToLoad) => {
    try {
      setLoading(true);
      const id = idToLoad || statementId;
      console.log('[WitnessStatementForm] Loading statement:', id);
      const response = await witnessStatementService.getStatement(id);
      console.log('[WitnessStatementForm] Loaded statement:', response);
      console.log('[WitnessStatementForm] Has draft_data:', !!response.draft_data);

      setStatement(response);

      if (response.draft_data) {
        console.log('[WitnessStatementForm] draft_data.witness:', response.draft_data.witness);
        console.log('[WitnessStatementForm] draft_data.narrative:', response.draft_data.narrative);
        console.log('[WitnessStatementForm] draft_data.declaration:', response.draft_data.declaration);

        setFormData({
          system_context_affirmed: response.draft_data.system_context_affirmed_at ? true : false,
          narrative: response.draft_data.narrative || formData.narrative,
          assertions: response.draft_data.assertions || [],
          declaration: response.draft_data.declaration || formData.declaration
        });
      }
    } catch (err) {
      console.error('Error loading statement:', err);
      setError(err.message || 'Failed to load statement');
    } finally {
      setLoading(false);
    }
  };

  const createNewStatement = async () => {
    try {
      setLoading(true);

      // Event ID is required for creating statements
      if (!userEventId) {
        throw new Error('Event ID is required to create a witness statement');
      }

      const response = await witnessStatementService.createStatement(templateId, userEventId);
      setStatement(response);
      // Witness data is in statement.draft_data.witness (immutable, set by backend)
    } catch (err) {
      console.error('Error creating statement:', err);
      // Extract detailed error message from API response
      const errorMessage = err.response?.data?.error || err.response?.data?.detail || err.message || 'Failed to create statement';
      const errorHint = err.response?.data?.hint;
      const fullError = errorHint ? `${errorMessage}\n${errorHint}` : errorMessage;
      setError(fullError);
    } finally {
      setLoading(false);
    }
  };

  const saveProgress = useCallback(async () => {
    if (!statement || statement.status !== 'draft') return;

    try {
      await witnessStatementService.updateStatement(statement.statement_id, {
        // Don't send witness - it's immutable system data set at creation
        narrative: formData.narrative,
        assertions: formData.assertions,
        declaration: formData.declaration
      });
      console.log('[WitnessStatementForm] Auto-saved');
    } catch (err) {
      console.error('[WitnessStatementForm] Auto-save failed:', err);
    }
  }, [statement, formData]);

  const handleNext = async () => {
    // Validate current step
    if (activeStep === 0 && !formData.system_context_affirmed) {
      setError('You must affirm the system context to continue');
      return;
    }

    if (activeStep === 1) {
      if (!formData.narrative.stated_time || !formData.narrative.stated_position || !formData.narrative.account) {
        setError('Please complete all narrative fields');
        return;
      }
    }

    if (activeStep === 3 && !formData.declaration.affirmed) {
      setError('You must affirm the declaration to submit');
      return;
    }

    setError(null);

    // Save progress before moving to next step
    await saveProgress();

    if (activeStep === steps.length - 1) {
      // Submit statement
      await handleSubmit();
    } else {
      setActiveStep((prevActiveStep) => prevActiveStep + 1);
    }
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleAffirmContext = async () => {
    try {
      await witnessStatementService.affirmContext(statement.statement_id);
      setFormData(prev => ({ ...prev, system_context_affirmed: true }));
    } catch (err) {
      console.error('Error affirming context:', err);
      setError(err.message || 'Failed to affirm context');
    }
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);

      // Final save
      await witnessStatementService.updateStatement(statement.statement_id, {
        // Don't send witness - it's immutable system data set at creation
        narrative: formData.narrative,
        assertions: formData.assertions,
        declaration: formData.declaration
      });

      // Submit for review
      await witnessStatementService.submitStatement(statement.statement_id);

      // Use onSave callback if provided (dialog mode), otherwise navigate (route mode)
      if (onSave) {
        onSave();
      } else {
        navigate('/statements');
      }
    } catch (err) {
      console.error('Error submitting statement:', err);
      setError(err.message || 'Failed to submit statement');
    } finally {
      setLoading(false);
    }
  };

  const handleFieldChange = (section, field, value) => {
    setFormData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));
  };

  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              System Recorded Context
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Review the information automatically recorded by the system. This data cannot be edited.
            </Typography>

            {statement?.draft_data?.source_event && (
              <Box>
                {/* Capture Image with Bounding Box */}
                {statement.draft_data.source_event.captureS3Key && (
                  <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
                    <Typography variant="subtitle2" gutterBottom>Captured Image</Typography>
                    <Box sx={{ position: 'relative', display: 'inline-block' }}>
                      <img
                        src={statement.draft_data.source_event.captureMetadata?.capturedImageUrl ||
                             `https://${statement.draft_data.source_event.captureS3Key}`}
                        alt="Captured scene"
                        style={{ maxWidth: '100%', height: 'auto', display: 'block' }}
                      />
                      {statement.draft_data.source_event.captureMetadata?.boundingBox && (
                        <Box
                          sx={{
                            position: 'absolute',
                            border: '3px solid #00ff00',
                            boxShadow: '0 0 10px rgba(0,255,0,0.5)',
                            left: `${statement.draft_data.source_event.captureMetadata.boundingBox.Left * 100}%`,
                            top: `${statement.draft_data.source_event.captureMetadata.boundingBox.Top * 100}%`,
                            width: `${statement.draft_data.source_event.captureMetadata.boundingBox.Width * 100}%`,
                            height: `${statement.draft_data.source_event.captureMetadata.boundingBox.Height * 100}%`,
                          }}
                        />
                      )}
                    </Box>
                    <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                      {new Date(statement.draft_data.source_event.event_timestamp).toLocaleString()}
                    </Typography>
                  </Paper>
                )}

                {/* Person Information */}
                {statement.draft_data.primary_person && (
                  <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
                    <Typography variant="subtitle2" gutterBottom>Identified Person</Typography>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                      {statement.draft_data.primary_person.image && (
                        <Box>
                          <img
                            src={statement.draft_data.source_event.captureMetadata?.personImage ||
                                 `https://reference-bucket/${statement.draft_data.primary_person.image}`}
                            alt="Person reference"
                            style={{ width: 120, height: 120, objectFit: 'cover', borderRadius: 4 }}
                          />
                        </Box>
                      )}
                      <Box sx={{ flex: 1 }}>
                        {statement.draft_data.primary_person.first_name && (
                          <Typography variant="body1" sx={{ fontWeight: 600 }}>
                            {statement.draft_data.primary_person.first_name} {statement.draft_data.primary_person.last_name}
                          </Typography>
                        )}
                        <Typography variant="body2" color="text.secondary">
                          ID: {statement.draft_data.primary_person.person_id}
                        </Typography>
                        {statement.draft_data.primary_person.headline && (
                          <Typography variant="body2">
                            {statement.draft_data.primary_person.headline}
                          </Typography>
                        )}
                        {statement.draft_data.primary_person.physical_attributes && (
                          <Typography variant="body2" sx={{ mt: 1 }}>
                            {statement.draft_data.primary_person.physical_attributes.gender}, {statement.draft_data.primary_person.physical_attributes.age_group}
                          </Typography>
                        )}
                        {statement.draft_data.source_event.captureMetadata?.matchAccuracy && (
                          <Typography variant="body2" color="success.main" sx={{ mt: 1 }}>
                            Match: {(statement.draft_data.source_event.captureMetadata.matchAccuracy * 100).toFixed(1)}%
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </Paper>
                )}

                {/* Event Details */}
                <Paper variant="outlined" sx={{ p: 2, mb: 3, bgcolor: 'grey.50' }}>
                  <Typography variant="subtitle2" gutterBottom>Event Details</Typography>
                  <Typography variant="body2">
                    <strong>Date/Time:</strong> {new Date(statement.draft_data.source_event.event_timestamp).toLocaleString()}
                  </Typography>
                  {statement.draft_data.source_event.captureMetadata?.address && (
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      <strong>Location:</strong> {statement.draft_data.source_event.captureMetadata.address.formatted ||
                                                   statement.draft_data.source_event.captureMetadata.address.short}
                    </Typography>
                  )}
                  {statement.draft_data.operator && (
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      <strong>Recorded by:</strong> {statement.draft_data.operator.handle || statement.draft_data.operator.user_id}
                    </Typography>
                  )}
                </Paper>
              </Box>
            )}

            {/* Template-driven fields (resolved tags) */}
            {statement?.draft_data?.template_snapshot?.config?.sections && (
              <Box sx={{ mt: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Template Fields
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Additional information from the template:
                </Typography>
                <TemplateRenderer
                  statement={statement}
                  formData={formData}
                  onFieldChange={handleFieldChange}
                />
              </Box>
            )}

            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.system_context_affirmed}
                  onChange={(e) => {
                    setFormData(prev => ({ ...prev, system_context_affirmed: e.target.checked }));
                    if (e.target.checked) {
                      handleAffirmContext();
                    }
                  }}
                />
              }
              label="I affirm that the above system context is correct"
              sx={{ mt: 3 }}
            />
          </Box>
        );

      case 1:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              What Happened
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Please provide your account of the incident.
            </Typography>

            <TextField
              fullWidth
              label="When did this happen?"
              value={formData.narrative.stated_time}
              onChange={(e) => handleFieldChange('narrative', 'stated_time', e.target.value)}
              margin="normal"
              required
              helperText="e.g., 'Around 2:30 PM today' or 'Yesterday morning'"
            />

            <TextField
              fullWidth
              label="Where were you positioned?"
              value={formData.narrative.stated_position}
              onChange={(e) => handleFieldChange('narrative', 'stated_position', e.target.value)}
              margin="normal"
              required
              helperText="e.g., 'Behind the counter' or 'Near the entrance'"
            />

            <TextField
              fullWidth
              label="What happened? (Your full account)"
              value={formData.narrative.account}
              onChange={(e) => handleFieldChange('narrative', 'account', e.target.value)}
              margin="normal"
              required
              multiline
              rows={6}
              helperText="Provide as much detail as you can remember"
            />
          </Box>
        );

      case 2:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Identification
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Details about any persons you identified. (This step can be completed later if needed)
            </Typography>

            <Alert severity="info">
              The ADVOKATE framework for identification assertions will be completed in the next version.
              You can skip this step for now and submit your statement.
            </Alert>
          </Box>
        );

      case 3:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Review & Submit
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Please review all information before submitting your statement.
            </Typography>

            <StatementReviewDisplay
              statement={statement}
              formData={{
                source_event: statement?.draft_data?.source_event,
                primary_person: statement?.draft_data?.primary_person,
                narrative: formData.narrative,
                witness: statement?.draft_data?.witness || formData.witness,
                declaration: formData.declaration
              }}
            />

            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.declaration.affirmed}
                  onChange={(e) => handleFieldChange('declaration', 'affirmed', e.target.checked)}
                />
              }
              label="I affirm this declaration"
            />

            <Alert severity="warning" sx={{ mt: 2 }}>
              Once submitted, you will not be able to edit this statement. Please review all information carefully.
            </Alert>
          </Box>
        );

      default:
        return 'Unknown step';
    }
  };

  if (loading && !statement) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto', p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Witness Statement
      </Typography>

      {statement && (
        <Typography variant="body2" color="text.secondary" gutterBottom>
          Statement ID: {statement.statement_id}
        </Typography>
      )}

      <Stepper activeStep={activeStep} sx={{ my: 4 }}>
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 3, minHeight: 400 }}>
        {renderStepContent(activeStep)}

        <Box sx={{ display: 'flex', flexDirection: 'row', pt: 3, mt: 3, borderTop: 1, borderColor: 'divider' }}>
          <Button
            color="inherit"
            disabled={activeStep === 0}
            onClick={handleBack}
            sx={{ mr: 1 }}
          >
            Back
          </Button>
          <Box sx={{ flex: '1 1 auto' }} />
          <Button
            onClick={handleNext}
            disabled={loading || (activeStep === steps.length - 1 && !formData.declaration.affirmed)}
          >
            {activeStep === steps.length - 1 ? 'Submit' : 'Next'}
          </Button>
        </Box>
      </Paper>

      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2, textAlign: 'center' }}>
        Your progress is automatically saved every 60 seconds
      </Typography>
    </Box>
  );
};

export default React.memo(WitnessStatementForm);
